<template>
  <v-dialog
    v-model="dialog"
    fullscreen
    transition="dialog-bottom-transition"
    scrollable
    v-if="configuration.summary"
  >
    <template v-slot:activator="{ on }">
      <v-card flat>
        <v-card-text>
          {{ $t('audit_summary')}}
          <v-textarea
            style="margin-top: 14px;"
            outline
            readonly
            prepend-inner-icon="edit"
            color="primary"
            placeholder="Es wurde noch kein Auditfazit hinterlegt"
            :value="currentAudit.summary"
            v-on="on"
          ></v-textarea>
        </v-card-text>
        <v-divider></v-divider>
      </v-card>
    </template>

    <v-card>
      <v-toolbar dark color="primary">
        <v-btn icon dark @click.native="close">
          <v-icon>close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ $t('toolbar_title')}}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn dark flat @click.native="save">{{ $t('save') }}</v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text>
        <v-container grid-list-md>
          <v-layout row wrap>
            <v-flex xs12>
              <v-textarea
                outline
                color="primary"
                :label="$t('auditsummary_label')"
                v-model="summaryModel"
              ></v-textarea>
            </v-flex>
            <v-flex xs12 v-if="hasAdditionalReportData">
              <MenuDatePicker
                :label="$t('audit_correction_due_date_label')"
                v-model="correctionDueDate"
                clearable
              />
            </v-flex>
            <v-flex xs12 v-if="hasAdditionalReportData">
              <MenuDatePicker
                :label="$t('audit_review_due_date_label')"
                v-model="reviewDueDate"
                clearable
              />
            </v-flex>
            <v-flex xs12 v-if="hasAdditionalReportData">
              <v-checkbox :label="$t('audit_requires_follow_up_label')" v-model="requiresFollowUp" />
              <MenuDatePicker
                v-if="requiresFollowUp"
                :label="$t('audit_requires_follow_up_date_label')"
                v-model="requiresFollowUpDate"
                clearable
              />
            </v-flex>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" flat @click.native="close">{{$t('cancel')}}</v-btn>
              <v-btn color="blue darken-1" flat @click.native="save">{{$t('save')}}</v-btn>
            </v-card-actions>
          </v-layout>
        </v-container>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<i18n>
{
  "de": {
    "toolbar_title": "Audit Zusammenfassung",
    "auditsummary_label": "Zusammenfassung des Audits",
    "save": "Speichern",
    "cancel": "Abbrechen",

    "audit_correction_due_date_label":"Erledigung der Korrekturen bis",
    "audit_review_due_date_label":"Vorlage der Korrekturen bis",
    "audit_requires_follow_up_label":"Nachaudit erforderlich",
    "audit_requires_follow_up_date_label":"Termin für das Nachaudit",

    "audit_summary": "Audit Zusammenfassung"

  },
  "en": {
    "toolbar_title": "Audit Summary",
    "auditsummary_label": "Summary of the Audit",
    "save": "Save",
    "cancel": "Cancel",

    "audit_correction_due_date_label":"Correction due date",
    "audit_review_due_date_label":"Correction review date",
    "audit_requires_follow_up_label":"Follow up audit required",
    "audit_requires_follow_up_date_label":"Follow up audit date",

    "audit_summary": "Audit Summary"
  }
}
</i18n>

<script>
import uuid from "uuid";

import { mapActions, mapGetters } from "vuex";
import { api } from "@/store/modules/audits";
import { api as confApi } from "@/store/modules/configuration";

import MenuDatePicker from "@/components/controls/MenuDatePicker";

export default {
  props: {
    currentAudit: {
      type: Object,
      required: true
    }
  },
  components: { MenuDatePicker },
  data() {
    return {
      dialog: false,
      summaryModel: "",
      correctionDueDate: "",
      reviewDueDate: "",
      requiresFollowUp: false,
      requiresFollowUpDate: ""
    };
  },
  computed: {
    ...mapGetters(confApi.namespace, [confApi.getters.audit]),
    configuration() {
      return this.audit.dialog || {};
    },
    hasAdditionalReportData() {
      return this.configuration.additionalReportData || false;
    },
    additionalReportData() {
      if (
        this.correctionDueDate === "" &&
        this.reviewDueDate === "" &&
        this.requiresFollowUp === false
      ) {
        return null;
      } else {
        return {
          correctionDueDate: this.correctionDueDate,
          reviewDueDate: this.reviewDueDate,
          requiresFollowUp:
            this.requiresFollowUp && this.requiresFollowUpDate !== ""
              ? this.requiresFollowUpDate
              : this.requiresFollowUp
        };
      }
    }
  },
  watch: {
    dialog(newVal) {
      this.correctionDueDate = "";
      this.reviewDueDate = "";
      this.requiresFollowUp = false;
      this.requiresFollowUpDate = "";
      this.summaryModel = "";

      if (newVal) {
        this.summaryModel = this.currentAudit.summary;
        const data = this.currentAudit.additionalReportData || null;
        if (data) {
          this.correctionDueDate = data.correctionDueDate;
          this.reviewDueDate = data.reviewDueDate;
          this.requiresFollowUpDate =
            typeof data.requiresFollowUp === "string"
              ? data.requiresFollowUp
              : "";
          this.requiresFollowUp = Boolean(data.requiresFollowUp);
        }
      }
    }
  },
  methods: {
    ...mapActions(api.namespace, [api.actions.updateAudit]),
    close() {
      this.dialog = false;
    },
    save() {
      const data = {
        summary: this.summaryModel,
        additionalReportData: this.additionalReportData
      };

      const payload = {
        data,
        audit_id: this.currentAudit.id,
        activity: {
          id: uuid.v4(),
          avatar: this.$user.avatar(),
          actor: this.$user.id(),
          actor_name: this.$user.displayName(),
          verb:
            this.$user.displayName() +
            " hat das Audit " +
            this.currentAudit.name +
            " bearbeitet",
          object: this.currentAudit.id,
          object_url: "/audit/" + this.currentAudit.id,
          timestamp: new Date().toISOString().slice(0, 10)
        }
      };

      this.updateAudit(payload);
      this.close();
    }
  }
};
</script>
